import React, { useState, useEffect, useMemo, useRef } from "react";
import { studentHostelsApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./StudentHostels.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const BLANK_FORM = {
  _id: null,
  Title: "",
  Subtitle: "",
  Description: "",
  Hostel_Facilities: [],
};

const StudentHostels = () => {
  const [hostels, setHostels]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(BLANK_FORM);
  const [confirmId, setConfirmId]           = useState(null);
  const [searchText, setSearchText]         = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => { fetchHostels(); }, []);

  const fetchHostels = async () => {
    try {
      setLoading(true);
      const response = await apiClient(studentHostelsApi);
      if (response.ok) {
        const result = await response.json();
        setHostels(result.data || []);
      } else {
        console.error("API Response not OK:", response.status);
      }
    } catch (error) {
      console.error("Error fetching hostels:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => hostels.filter(h => {
    const q = searchText.toLowerCase();
    return (h.Title || "").toLowerCase().includes(q) || (h.Subtitle || "").toLowerCase().includes(q);
  }), [hostels, searchText]);

  const stats = useMemo(() => ({
    total: hostels.length,
    facilities: hostels.reduce((acc, curr) => acc + (curr.Hostel_Facilities?.length || 0), 0),
    latest: hostels.length > 0 ? hostels[0].Title : "None"
  }), [hostels]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 180 });
    setDropdownOpenId(id);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? studentHostelsApi : `${studentHostelsApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(formData),
      });
      if (response.ok) { fetchHostels(); setModalType(""); }
    } catch (error) {
      console.error("Error saving hostel:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${studentHostelsApi}/${id}`, { method: "DELETE" });
      if (response.ok) { fetchHostels(); setConfirmId(null); }
    } catch (error) {
      console.error("Error deleting hostel:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFacilityChange = (idx, val) => {
    const next = [...formData.Hostel_Facilities];
    next[idx] = val;
    setFormData({ ...formData, Hostel_Facilities: next });
  };

  const addFacility = () => setFormData({ ...formData, Hostel_Facilities: [...formData.Hostel_Facilities, ""] });
  const removeFacility = (idx) => setFormData({ ...formData, Hostel_Facilities: formData.Hostel_Facilities.filter((_, i) => i !== idx) });

  return (
    <div className="sh-page">
      {/* ── Header ── */}
      <div className="sh-header">
        <div className="sh-header-left">
          <div className="sh-breadcrumb">
            <span>Facilities</span>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
            <span className="sh-bc-active">Student Hostels</span>
          </div>
          <h1>Student Hostels</h1>
          <p>Manage hostel blocks, accommodation types, and student facilities for both boys and girls campuses.</p>
        </div>
        <button type="button" className="sh-add-btn" onClick={() => { setFormData(BLANK_FORM); setModalType("add"); }}>
          + Add Hostel Block
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="sh-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <div className="sh-card" style={{ padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <div className="sh-icon" style={{ width: 40, height: 40, fontSize: 18 }}>🏠</div>
          <div className="sh-meta"><h3>{stats.total}</h3><p>Total Blocks</p></div>
        </div>
        <div className="sh-card" style={{ padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <div className="sh-icon" style={{ width: 40, height: 40, fontSize: 18, background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)' }}>✨</div>
          <div className="sh-meta"><h3>{stats.facilities}</h3><p>Total Facilities</p></div>
        </div>
        <div className="sh-card" style={{ padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <div className="sh-icon" style={{ width: 40, height: 40, fontSize: 18, background: 'linear-gradient(135deg, #10b981, #34d399)' }}>🆕</div>
          <div className="sh-meta"><h3>{stats.latest}</h3><p>Recently Added</p></div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="sh-toolbar">
        <input type="text" placeholder="Search hostel blocks by title or location..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
      </div>

      {loading && <div className="loading-overlay" style={{ textAlign:'center', padding:40 }}>Loading hostel records...</div>}

      {/* ── Grid ── */}
      <div className="sh-grid">
        {filtered.map((item) => (
          <div key={item._id} className="sh-card" onClick={() => { setFormData(item); setModalType("edit"); }}>
            <div className="sh-card-head">
              <div className="sh-icon">🏢</div>
              <div className="sh-meta">
                <h3>{item.Title}</h3>
                <p>{item.Subtitle}</p>
              </div>
              <button type="button" className="sh-dots" onClick={(e) => handleDotsClick(e, item._id)}>⋮</button>
            </div>
            <p className="sh-description">{item.Description}</p>
            <div className="sh-facilities">
              {(item.Hostel_Facilities || []).slice(0, 4).map((f, i) => (
                <span key={i} className="sh-pill">{f}</span>
              ))}
              {(item.Hostel_Facilities?.length || 0) > 4 && (
                <span className="sh-more">+{item.Hostel_Facilities.length - 4} more</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Empty State ── */}
      {!loading && filtered.length === 0 && (
        <div className="sh-empty">
          <h3>No hostel blocks found</h3>
          <p>{searchText ? "No records match your search criteria." : "Click '+ Add Hostel Block' to create your first record."}</p>
        </div>
      )}

      {/* ── Dropdown ── */}
      {dropdownOpenId && (
        <div ref={dropdownRef} className="sh-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          <button type="button" onClick={() => { setFormData(hostels.find(h => h._id === dropdownOpenId)); setModalType("edit"); setDropdownOpenId(null); }}>Edit Details</button>
          <button type="button" className="danger" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete Block</button>
        </div>
      )}

      {/* ── Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="sh-overlay" onClick={() => setModalType("")}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sh-modal-head">
              <div>
                <h2>{modalType === "add" ? "Add Hostel Block" : "Edit Hostel Block"}</h2>
                <p>Manage accommodation details and available student facilities.</p>
              </div>
              <button type="button" className="sh-close" onClick={() => setModalType("")}>×</button>
            </div>
            <form className="sh-form" onSubmit={handleSave}>
              <div className="sh-field full">
                <label>Block Title / Name</label>
                <input value={formData.Title || ""} onChange={(e) => setFormData({ ...formData, Title: e.target.value })} placeholder="e.g. Boys Hostel - Block A" required />
              </div>
              <div className="sh-field full">
                <label>Subtitle / Location</label>
                <input value={formData.Subtitle || ""} onChange={(e) => setFormData({ ...formData, Subtitle: e.target.value })} placeholder="e.g. West Campus, Near Central Library" />
              </div>
              <div className="sh-field full">
                <label>Hostel Description</label>
                <textarea value={formData.Description || ""} onChange={(e) => setFormData({ ...formData, Description: e.target.value })} placeholder="Describe capacity, room types, and overall environment..." />
              </div>

              <div className="sh-field full">
                <div className="sh-facility-header">
                  <label>Hostel Facilities</label>
                  <button type="button" className="sh-mini-btn" onClick={addFacility}>+ Add Facility</button>
                </div>
                <div className="sh-facility-list">
                  {formData.Hostel_Facilities.map((f, i) => (
                    <div key={i} className="sh-facility-row">
                      <input value={f} onChange={(e) => handleFacilityChange(i, e.target.value)} placeholder="e.g. 24/7 Power Backup" />
                      <button type="button" className="sh-remove-btn" onClick={() => removeFacility(i)}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sh-actions">
                <button type="button" className="btn-light" onClick={() => setModalType("")}>Cancel</button>
                <button type="submit" className="btn-primary">{modalType === "add" ? "Create Block" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm ── */}
      {confirmId && (
        <div className="sh-overlay" onClick={() => setConfirmId(null)}>
          <div className="sh-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to permanently delete this hostel block? This action cannot be undone.</p>
            <div className="sh-actions">
              <button type="button" className="btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button type="button" className="btn-danger" onClick={() => handleDelete(confirmId)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentHostels;